/**
 * Cart Drawer
 *
 * Server-rendered inside the overlay section group.
 * Uses event delegation so all listeners survive DOM refreshes.
 * Section Rendering API (`?sections=`) keeps the drawer in sync.
 */

/* ── helpers ─────────────────────────────────────────────────── */

var _cartNotificationTimeout = null;

function showCartNotification(variantId) {
  // Close the shop the look popup immediately
  document.body.classList.remove('open-shop-look');
  
  var popup = document.querySelector('.cart_popup');
  if (!popup) return;

  if (_cartNotificationTimeout) {
    clearTimeout(_cartNotificationTimeout);
  }

  // Update cart count and drawer when showing notification
  updateCartCount();
  refreshCart();

  fetch(window.Shopify.routes.root + '?sections=cart-notification')
    .then(function (response) { return response.json(); })
    .then(function (data) {
      var html = data['cart-notification'];
      if (!html) return;

      popup.innerHTML = html;

      var items = popup.querySelectorAll('.cn_list');
      if (variantId) {
        var found = false;
        items.forEach(function(item) {
          if (item.classList.contains('cn_' + variantId)) {
            item.style.display = '';
            found = true;
          } else {
            item.style.display = 'none';
          }
        });
        if (!found && items.length > 0) {
          items.forEach(function(item, index) {
            item.style.display = index === 0 ? '' : 'none';
          });
        }
      } else {
        items.forEach(function(item, index) {
          item.style.display = index === 0 ? '' : 'none';
        });
      }

      // Force a browser reflow/repaint to trigger the opacity transition
      void popup.offsetHeight;

      popup.classList.add('active');
      document.body.classList.add('cart-notification-open');
      setAtcButtonText(true);

      _cartNotificationTimeout = setTimeout(function () {
        popup.classList.remove('active');
        document.body.classList.remove('cart-notification-open');
        setAtcButtonText(false);
      }, 5000);
    })
    .catch(console.error);
}

function setAtcButtonText(isAdded) {
  var atcBtn = document.querySelector('.product-atc');
  if (!atcBtn) return;
  var textEl = atcBtn.querySelector('.product-atc__text');
  var newText = isAdded
    ? (atcBtn.getAttribute('data-added-text') || 'Added to bag')
    : (atcBtn.getAttribute('data-add-text') || 'Add to cart');

  if (textEl) {
    textEl.textContent = newText;
  } else {
    // Button has no child text wrapper — update only the text node to preserve the SVG icon
    for (var i = 0; i < atcBtn.childNodes.length; i++) {
      var node = atcBtn.childNodes[i];
      if (node.nodeType === 3 && node.textContent.trim()) {
        node.textContent = ' ' + newText + ' ';
        break;
      }
    }
  }

  if (isAdded) {
    atcBtn.classList.add('is-added');
  } else {
    atcBtn.classList.remove('is-added');
  }
}

function getCartSectionIds() {
  var els = document.querySelectorAll('[data-cart-section-id]');
  return Array.from(els).map(function(el) { return el.dataset.cartSectionId; });
}

function updateCartCount() {
  fetch('/cart.js')
    .then(function (r) { return r.json(); })
    .then(function (data) {  
      if(typeof window.updateCartCountDisplay === 'function') {
        window.updateCartCountDisplay(data.item_count);
      } else {
        var countVal = parseInt(data.item_count, 10) || 0;
        document.querySelectorAll('.header-cart').forEach(function(btn) {
          if (countVal > 0) {
            btn.classList.add('has-items');
          } else {
            btn.classList.remove('has-items');
          }
        });

        document.querySelectorAll('.cart-count').forEach(function (el) {
          if (countVal > 0) {
            el.innerHTML = countVal;
          } else {
            el.innerHTML = '';
          }
        });
      }
    })
    .catch(console.error);
}

/* ── drawer open / close with focus‑trap (WCAG) ──────────────── */

var _previouslyFocused = null;

function openCartDrawer() {
  var drawer = document.querySelector('.cart-sidebar');
  if (!drawer) return;

  _previouslyFocused = document.activeElement;

  document.body.classList.add('open-drawer');
  document.body.classList.remove('open-menu', 'open-search', 'open-sort', 'open-filter', 'open-search-drawer');

  drawer.setAttribute('aria-hidden', 'false');
  drawer.focus();
}

function closeCartDrawer() {
  var drawer = document.querySelector('.cart-sidebar');
  if (!drawer) return;

  document.body.classList.remove('open-drawer', 'open-menu', 'open-search', 'open-sort', 'open-filter', 'open-shop-look');
  drawer.setAttribute('aria-hidden', 'true');

  if (_previouslyFocused && _previouslyFocused.focus) {
    _previouslyFocused.focus();
  }
}

/* ── core: refresh cart drawer via Section Rendering API ───── */

/**
 * Light cart-page update — qty change only.
 * Only patches prices/subtotals. Never touches recommendations or reinitialises sliders.
 */
function refreshCartPageQty() {
  var cartSection = document.querySelector('[data-cart-page-section]');
  if (!cartSection) return Promise.resolve();

  var sectionId = cartSection.closest('[id^="shopify-section-"]').id.replace('shopify-section-', '');

  return fetch(window.Shopify.routes.root + '?sections=' + sectionId)
    .then(function(r) { return r.json(); })
    .then(function(data) {
      var html  = new DOMParser().parseFromString(data[sectionId], 'text/html');
      var fresh = html.querySelector('[data-cart-page-section]');
      if (!fresh) return;

      ['.cart-items-list', '.cart-summary'].forEach(function(sel) {
        var cur = cartSection.querySelector(sel);
        var nxt = fresh.querySelector(sel);
        if (cur && nxt) cur.innerHTML = nxt.innerHTML;
      });
    });
}

/**
 * Full cart-page update — item added/removed/variant-swapped, or discount applied.
 * Updates items, summary, and reloads recommendations when the item count or
 * driving product changes.
 */
function refreshCartPage() {
  var cartSection = document.querySelector('[data-cart-page-section]');
  if (!cartSection) return Promise.resolve();

  var sectionId = cartSection.closest('[id^="shopify-section-"]').id.replace('shopify-section-', '');

  return fetch(window.Shopify.routes.root + '?sections=' + sectionId)
    .then(function(response) { return response.json(); })
    .then(function(data) {
      var html  = new DOMParser().parseFromString(data[sectionId], 'text/html');
      var fresh = html.querySelector('[data-cart-page-section]');
      if (!fresh) return;

      // Capture counts and rec URL BEFORE replacing DOM so comparisons are valid.
      var currentRec   = cartSection.querySelector('product-recommendations[data-url]');
      var freshRec     = fresh.querySelector('product-recommendations[data-url]');
      var currentItems = cartSection.querySelectorAll('.cart-items-list .item-row').length;
      var freshItems   = fresh.querySelectorAll('.cart-items-list .item-row').length;

      // Surgically update items and totals — never replaces the
      // <product-recommendations> element itself.
      ['.cart-items-list', '.cart-summary', '.cart-actions'].forEach(function(sel) {
        var cur = cartSection.querySelector(sel);
        var nxt = fresh.querySelector(sel);
        if (cur && nxt) cur.innerHTML = nxt.innerHTML;
      });

      // Reload recommendations only when item count or driving product changed.
      if (currentRec && freshRec) {
        if (currentRec.dataset.url !== freshRec.dataset.url || currentItems !== freshItems) {
          currentRec.dataset.url = freshRec.dataset.url;
          if (typeof currentRec.forceLoadRecommendations === 'function') {
            currentRec.forceLoadRecommendations();
          }
        }
      }

      initMatchItSlider();
      initCartDrawerSlider();
      if (typeof initProductCardSliders === 'function') initProductCardSliders();
      if (typeof quickAddButton === 'function') quickAddButton();
      initUspTicker();
      initCartAccordion();
      if (typeof initQuantitySelectors === 'function') initQuantitySelectors();
    });
}

function refreshCart() {
  var sectionIds = getCartSectionIds();
  if (!sectionIds.length) return Promise.resolve();

  var query = sectionIds.join(',');

  return fetch(window.Shopify.routes.root + '?sections=' + query)
    .then(function (response) { return response.json(); })
    .then(function (data) {
      sectionIds.forEach(function(sectionId) {
        if (!data[sectionId]) return;

        var wrapper = document.querySelector('[data-cart-section-id="' + sectionId + '"]');
        if (!wrapper) {
            wrapper = document.getElementById('shopify-section-' + sectionId);
        }
        
        if (!wrapper) return;

        var parsed = new DOMParser().parseFromString(data[sectionId], 'text/html');
        var fresh  = parsed.querySelector('[data-cart-section-id="' + sectionId + '"]') || parsed.getElementById('shopify-section-' + sectionId);
        
        if (fresh) {
          var cartBody = wrapper.querySelector('.cart-drawer-body');
          var scrollTop = cartBody ? cartBody.scrollTop : 0;
          if (cartBody) cartBody.style.minHeight = cartBody.scrollHeight + 'px';

          var currentEmpty = wrapper.querySelector('.cart-drawer-empty');
          var freshEmpty = fresh.querySelector('.cart-drawer-empty');
          
          if ((currentEmpty && !freshEmpty) || (!currentEmpty && freshEmpty)) {
            // State changed from empty to non-empty or vice-versa
            wrapper.innerHTML = fresh.innerHTML;
          } else if (!currentEmpty && !freshEmpty) {
            // Selectively update dynamic parts to avoid destroying sliders/tickers.
            // NOTE: .cart-drawer-body is intentionally excluded — replacing it destroys
            // the <product-recommendations> custom element and triggers a fresh
            // recommendation fetch on every quantity/variant change.
            var selectors = [
              '.drawer-header',
              '.free-shipping-bar',
              '.drawer-fixed-bottom'
            ];
            selectors.forEach(function(selector) {
              var currentEl = wrapper.querySelector(selector);
              var freshEl = fresh.querySelector(selector);
              if (currentEl && freshEl) {
                currentEl.innerHTML = freshEl.innerHTML;
              }
            });

            var currentMiddle = wrapper.querySelector('.drawer-middle');
            var freshMiddle = fresh.querySelector('.drawer-middle');
            if (currentMiddle && freshMiddle) {
               var cRows = Array.prototype.slice.call(currentMiddle.querySelectorAll('.item-row'));
               var fRows = Array.prototype.slice.call(freshMiddle.querySelectorAll('.item-row'));
               
               // Step 1: Remove rows that no longer exist in the new cart state (e.g. removed item)
               cRows.forEach(function(cRow) {
                 var existsInFresh = fRows.find(function(f) { return f.dataset.variantId === cRow.dataset.variantId; });
                 if (!existsInFresh) {
                   cRow.parentNode.removeChild(cRow);
                 }
               });

               // Step 2: Iterate over fresh rows and sync them into the current DOM
               fRows.forEach(function(fRow, index) {
                  var cRow = currentMiddle.children[index];
                  
                  if (cRow && cRow.dataset.variantId === fRow.dataset.variantId) {
                     // Granular update for matching row
                     var cQty = cRow.querySelector('.qty-input');
                     var fQty = fRow.querySelector('.qty-input');
                     if (cQty && fQty) cQty.value = fQty.value;

                     var cMinus = cRow.querySelector('.qty-minus');
                     var fMinus = fRow.querySelector('.qty-minus');
                     if (cMinus && fMinus) {
                       if (fMinus.hasAttribute('disabled')) {
                         cMinus.setAttribute('disabled', 'disabled');
                       } else {
                         cMinus.removeAttribute('disabled');
                       }
                     }
                     
                     var cPrice = cRow.querySelector('.item-price');
                     var fPrice = fRow.querySelector('.item-price');
                     if (cPrice && fPrice) cPrice.innerHTML = fPrice.innerHTML;

                     var cStock = cRow.querySelector('.stock-urgency-msg');
                     var fStock = fRow.querySelector('.stock-urgency-msg');
                     if (cStock && fStock) {
                        cStock.innerHTML = fStock.innerHTML;
                     } else if (fStock) {
                        var cProductType = cRow.querySelector('.item-product-type');
                        if (cProductType) {
                           cProductType.insertAdjacentHTML('afterend', fStock.outerHTML);
                        } else {
                           var cMain = cRow.querySelector('.item__main');
                           if (cMain) cMain.appendChild(fStock.cloneNode(true));
                        }
                     } else if (cStock) {
                        cStock.parentNode.removeChild(cStock);
                     }

                     var cSelected = cRow.querySelector('.selected-value');
                     var fSelected = fRow.querySelector('.selected-value');
                     if (cSelected && fSelected) cSelected.innerHTML = fSelected.innerHTML;

                     var cOptions = cRow.querySelector('.selector-options');
                     var fOptions = fRow.querySelector('.selector-options');
                     if (cOptions && fOptions) cOptions.innerHTML = fOptions.innerHTML;

                     // Update the index lines so JS continues to target the correct item
                     cRow.dataset.line = fRow.dataset.line;
                     var cRemove = cRow.querySelector('.js-item-remove');
                     var fRemove = fRow.querySelector('.js-item-remove');
                     if (cRemove && fRemove) cRemove.dataset.line = fRemove.dataset.line;

                     var cVariantDrop = cRow.querySelector('.js-custom-variant-selector');
                     var fVariantDrop = fRow.querySelector('.js-custom-variant-selector');
                     if (cVariantDrop && fVariantDrop) cVariantDrop.dataset.line = fVariantDrop.dataset.line;

                  } else {
                     // New row or shifted different variant. Insert the new HTML.
                     if (cRow) {
                        cRow.insertAdjacentHTML('beforebegin', fRow.outerHTML);
                     } else {
                        currentMiddle.appendChild(fRow.cloneNode(true));
                     }
                  }
               });

               // Step 3: Cleanup any extra dangling rows at the bottom
               while(currentMiddle.children.length > fRows.length) {
                  currentMiddle.removeChild(currentMiddle.lastElementChild);
               }

               // Reload recommendations when item count changed (product added or removed).
               // Skip for qty/variant changes (same count) to avoid flickering the section.
               var currentRec = wrapper.querySelector('product-recommendations[data-url]');
               var freshRec   = fresh.querySelector('product-recommendations[data-url]');
               if (currentRec && freshRec) {
                 var urlChanged = currentRec.dataset.url !== freshRec.dataset.url;
                 var countChanged = cRows.length !== fRows.length;
                 if (urlChanged || countChanged) {
                   currentRec.dataset.url = freshRec.dataset.url;
                   if (typeof currentRec.forceLoadRecommendations === 'function') {
                     currentRec.forceLoadRecommendations();
                   }
                 }
               }
            }
          } else {
            // Both are empty
            wrapper.innerHTML = fresh.innerHTML;
          }

          var updatedCartBody = wrapper.querySelector('.cart-drawer-body');
          if (updatedCartBody) {
            updatedCartBody.style.minHeight = '';
            updatedCartBody.scrollTop = scrollTop;
          }
        }
      });

      /* keep drawer visibly open if it was open */
      if (document.body.classList.contains('open-drawer')) {
        var newDrawer = document.querySelector('.cart-sidebar');
        if (newDrawer) newDrawer.setAttribute('aria-hidden', 'false');
      }

      updateCartCount();
      initMatchItSlider();
      initCartDrawerSlider();
      initDiscountInput();
      if (typeof initProductCardSliders === 'function') {
        initProductCardSliders();
      }
      if (typeof quickAddButton === 'function') {
        quickAddButton();
      }
      initUspTicker();
      initCartAccordion();

      var cartDrawer = document.querySelector('.cart-drawer');
      if (cartDrawer) cartDrawer.classList.remove('is-updating');
    })
    .catch(function (error) {
      console.error('Error refreshing cart:', error);
    });
}

/** Backward-compatible alias – theme.js calls load_cart() */
function load_cart() {
  return refreshCart();
}

/* ── Splide init (scoped to cart drawer) ─────────────────────── */

function initMatchItSlider() {
  if (typeof Splide === 'undefined') return;

  document.querySelectorAll('.product-recommendations-products.splide').forEach(function (slider) {

    if (slider.classList.contains('is-initialized')) return;

    var splideList = slider.querySelector('.splide__list');
    var slideCount = splideList ? splideList.children.length : 0;

    // Parent wrapper
    var wrapper = slider.closest('product-recommendations');

    // Add/remove class based on whether arrows will be shown
    if (window.innerWidth > 1199) {
      wrapper.classList.toggle('no-slider', slideCount <= 4);
    } else if (window.innerWidth > 991) {
      wrapper.classList.toggle('no-slider', slideCount <= 3);
    } else {
      wrapper.classList.toggle('no-slider', slideCount <= 2);
    }

    if (slider.splide) {
      slider.splide.destroy(true);
    }

    slider.querySelectorAll('.splide__pagination').forEach(function (pagination) {
      pagination.remove();
    });

    slider.classList.remove('is-initialized');

    var splide = new Splide(slider, {
      type: 'slide',
      focus: 0,
      drag: slideCount > 4 ? true : false,
      snap: true,
      speed: 400,
      omitEnd: true,
      perPage: 4,
      perMove: 1,
      gap: '10px',
      pagination: slideCount > 1,
      arrows: false, // Disable Splide's default arrows
      mediaQuery: 'max',
      breakpoints: {
        1199: {
          perPage: 3,
          pagination: slideCount > 1,
          drag: slideCount > 3 ? true : false
        },
        991: {
          perPage: 2,
          pagination: slideCount > 1,
          drag: slideCount > 2 ? true : false
        }
      }
    });

    var isScrolling = false;
    var scrollTimeout = null;
    slider.addEventListener('wheel', function (e) {
      var absX = Math.abs(e.deltaX);
      var absY = Math.abs(e.deltaY);
      if (absX > absY && absX > 5) {
        e.preventDefault();
        if (!isScrolling) {
          isScrolling = true;
          if (e.deltaX > 0) splide.go('>');
          else if (e.deltaX < 0) splide.go('<');
        }
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(function () { isScrolling = false; }, 200);
      }
    }, { passive: false });

    // Custom arrows
    const prevBtn = wrapper.querySelector('.pr__arrow--prev');
    const nextBtn = wrapper.querySelector('.pr__arrow--next');

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        splide.go('<');
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        splide.go('>');
      });
    }

    // Update active/inactive arrow state
    function updateArrows() {
      let perPage = 4;

      if (window.innerWidth <= 991) {
        perPage = 2;
      } else if (window.innerWidth <= 1199) {
        perPage = 3;
      }

      prevBtn?.classList.toggle('is-inactive', splide.index === 0);
      nextBtn?.classList.toggle(
        'is-inactive',
        splide.index >= splide.length - perPage
      );
    }

    splide.on('mounted move resized', updateArrows);

    splide.mount();

    slider.classList.add('is-initialized');
  });
}

document.addEventListener('DOMContentLoaded', initMatchItSlider);

function initCartDrawerSlider() {
  if (typeof Splide === 'undefined') return;

  document.querySelectorAll('.cart-drawer-slider').forEach(function (slider) {

    if (slider.classList.contains('is-initialized')) return;

    const splideList = slider.querySelector('.splide__list');
    const slideCount = splideList ? splideList.children.length : 0;
    const wrapper = slider.closest('product-recommendations') || slider.closest('.cart-drawer-match-it');

    if (!wrapper) return;

    // Hide arrows if slider isn't needed
    wrapper.classList.toggle('no-slider', slideCount <= 2);

    const splide = new Splide(slider, {
      type: 'slide',
      focus: 0,
      drag: slideCount > 1 ? true : false,
      snap: true,
      speed: 400,
      omitEnd: true,
      perPage: 2.5,
      padding: { left: '40px', right: '40px' },
      perMove: 1,
      gap: '10px',
      arrows: false,
      pagination: slideCount > 1,
      breakpoints: {
        767: {
          perPage: 2,
          padding: { left: '10px', right: '10px' },
          drag: slideCount > 1 ? true : false,
          pagination: slideCount > 1
        },
      },
    });

    var isScrolling2 = false;
    var scrollTimeout2 = null;
    slider.addEventListener('wheel', function (e) {
      var absX = Math.abs(e.deltaX);
      var absY = Math.abs(e.deltaY);
      if (absX > absY && absX > 5) {
        e.preventDefault();
        if (!isScrolling2) {
          isScrolling2 = true;
          if (e.deltaX > 0) splide.go('>');
          else if (e.deltaX < 0) splide.go('<');
        }
        clearTimeout(scrollTimeout2);
        scrollTimeout2 = setTimeout(function () { isScrolling2 = false; }, 200);
      }
    }, { passive: false });

    const prev = wrapper.querySelector('.pr__arrow--prev');
    const next = wrapper.querySelector('.pr__arrow--next');

    if (prev) {
      prev.addEventListener('click', function () {
        splide.go('<');
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        splide.go('>');
      });
    }

    function updateArrows() {
      const perPage = window.innerWidth <= 767 ? 2 : 2.5;

      if (prev) {
        prev.classList.toggle('is-inactive', splide.index === 0);
      }

      if (next) {
        next.classList.toggle(
          'is-inactive',
          splide.index >= splide.length - Math.floor(perPage)
        );
      }
    }

    splide.on('mounted move resized', updateArrows);

    splide.mount();

    slider.classList.add('is-initialized');
  });
}

document.addEventListener('DOMContentLoaded', initCartDrawerSlider);

let resizeTimer;
window.addEventListener('resize', function () {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(function () {
    // Cart page sliders
    document.querySelectorAll('.product-recommendations-products.splide').forEach(function (slider) {
      if (slider.splide) {
        slider.splide.destroy(true);
      }
      slider.querySelectorAll('.splide__pagination').forEach(function (pagination) {
        pagination.remove();
      });
      slider.classList.remove('is-initialized');
    });
    // Cart drawer sliders
    document.querySelectorAll('.cart-drawer-slider.splide').forEach(function (slider) {
      if (slider.splide) {
        slider.splide.destroy(true);
      }
      slider.querySelectorAll('.splide__pagination').forEach(function (pagination) {
        pagination.remove();
      });
      slider.classList.remove('is-initialized');
    });
    // Reinitialize both sliders
    if (typeof initMatchItSlider === 'function') {
      initMatchItSlider();
    }
    if (typeof initCartDrawerSlider === 'function') {
      initCartDrawerSlider();
    }
  }, 200);
});

/* ── cart mutations ───────────────────────────────────────────── */

function updateCartItemQuantity(line, quantity) {
  var cartDrawer = document.querySelector('.cart-drawer');
  if (cartDrawer) cartDrawer.classList.add('is-updating');

  fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ line: line, quantity: quantity })
  })
    .then(function (r) { return r.json(); })
    .then(function () { return refreshCart(); })
    .then(function () {
      if (!document.querySelector('[data-cart-page-section]')) return;
      // Removal (qty === 0) → full refresh so recommendations update for new first item.
      // Qty change only → lightweight update, never touches recommendations.
      if (quantity === 0) {
        refreshCartPage();
      } else {
        refreshCartPageQty();
      }
    })
    .catch(function (e) { console.error('Error updating cart:', e); });
}

/* ── discount helpers ────────────────────────────────────────── */

function fetchCurrentDiscountCodes() {
  return fetch('/cart.js', { method: 'GET' })
    .then(function (r) { return r.json(); })
    .then(function (data) {
      var codes = [];
      if (data.cart_level_discount_applications) {
        data.cart_level_discount_applications.forEach(function (d) {
          if (d.type === 'discount_code') codes.push(d.title);
        });
      }
      if (data.items) {
        data.items.forEach(function (item) {
          if (item.line_level_discount_allocations && item.line_level_discount_allocations.length) {
            item.line_level_discount_allocations.forEach(function (d) {
              if (d.discount_application && d.discount_application.type === 'discount_code') {
                codes.push(d.discount_application.title);
              }
            });
          }
        });
      }
      return codes.filter(function (v, i, a) { return a.indexOf(v) === i; });
    });
}

function applyDiscountCode(discountCode, btn) {
  var wrapper = btn ? btn.closest('.custom-discount-form') : document.querySelector('.custom-discount-form');
  var errorEl = wrapper ? wrapper.querySelector('p.error') : document.querySelector('.custom-discount-form p.error');
  
  if (btn) {
    btn.classList.add('loading');
    btn.disabled = true;
  }

  fetchCurrentDiscountCodes().then(function (codes) {
    // Avoid duplicates
    if (codes.indexOf(discountCode) === -1) {
      codes.push(discountCode);
    }

    fetch('/checkout?discount=' + codes.join(','), { method: 'GET', redirect: 'manual' })
      .finally(function () {
        fetch('/cart.js', { method: 'GET' })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var applied = [];
            if (data.cart_level_discount_applications) {
              data.cart_level_discount_applications.forEach(function (d) {
                if (d.type === 'discount_code') applied.push(d.title);
              });
            }
            data.items.forEach(function (item) {
              if (item.line_level_discount_allocations) {
                item.line_level_discount_allocations.forEach(function (d) {
                  if (d.discount_application && d.discount_application.type === 'discount_code') {
                    applied.push(d.discount_application.title);
                  }
                });
              }
            });
            
            // Case-insensitive check for application success
            var isApplied = applied.some(function(c) { 
              return c.toLowerCase() === discountCode.toLowerCase(); 
            });

            if (!isApplied) {
              if (errorEl) {
                errorEl.style.display = 'block';
                errorEl.textContent = wrapper.dataset.errorMsg || 'Enter a valid discount code or gift card';
              }
              if (btn) {
                btn.classList.remove('loading');
                btn.disabled = false;
              }
            } else {
              refreshCart().then(function() {
                var isCartPage = window.location.pathname.includes('/cart') || document.querySelector('[data-cart-page-section]');
                if (isCartPage && typeof refreshCartPage == 'function') {
                  refreshCartPage();
                }
              });
            }
          })
          .catch(function() {
            if (btn) {
              btn.classList.remove('loading');
              btn.disabled = false;
            }
          });
      });
  });
}

function removeDiscountCode(code) {
  fetchCurrentDiscountCodes().then(function (codes) {
    var remaining = codes.filter(function (c) { return c !== code; });
    fetch('/checkout?discount=' + remaining.join(','), { method: 'GET', redirect: 'manual' })
      .finally(function () { 
        refreshCart().then(function() {
          var isCartPage = window.location.pathname.includes('/cart') || document.querySelector('[data-cart-page-section]');
          if (isCartPage && typeof refreshCartPage == 'function') {
            refreshCartPage();
          }
        });
      });
  });
}

/* ── delegated click handler (survives DOM refreshes) ────────── */

function selectCartVariantOption(option) {
  var parent = option.closest('.js-custom-variant-selector');
  var newVariantId = option.dataset.value;

  if (option.classList.contains('is-active')) {
    parent.classList.remove('is-open');
    return;
  }

  var row = option.closest('.item-row');
  var activeOption = parent.querySelector('.selector-option.is-active');
  var oldVariantId = (row && row.dataset.variantId) || (activeOption && activeOption.dataset.value);
  var line = row ? row.dataset.line : null;

  // Show a loading state on the row and the drawer while it is removed + re-added (cleared on refresh).
  if (row) row.classList.add('is-updating');
  var cartDrawer = document.querySelector('.cart-drawer');
  if (cartDrawer) cartDrawer.classList.add('is-updating');

  updateCartItemVariant(oldVariantId, newVariantId, line);
  parent.classList.remove('is-open');
}

document.addEventListener('click', function (e) {

  /* Open cart drawer */
  if (e.target.closest('.header-cart')) {
    e.preventDefault();
    openCartDrawer();
    return;
  }

  /* View bag click inside cart notification opens the drawer and closes the notification */
  if (e.target.closest('.buttton-cart-bad')) {
    e.preventDefault();
    var popup = document.querySelector('.cart_popup');
    if (popup) {
      popup.classList.remove('active');
      document.body.classList.remove('cart-notification-open');
    }
    setAtcButtonText(false);
    openCartDrawer();
    return;
  }

  /* Close cart notification */
  if (e.target.closest('.close-cn')) {
    e.preventDefault();
    var popup = document.querySelector('.cart_popup');
    if (popup) {
      popup.classList.remove('active');
      document.body.classList.remove('cart-notification-open');
    }
    setAtcButtonText(false);
    return;
  }

  /* Close cart drawer */
  if (e.target.closest('.overlay-box') || e.target.closest('.cart-drawer-close')) {
    closeCartDrawer();
    return;
  }

  /* Remove line item */
  var removeBtn = e.target.closest('.js-item-remove');
  if (removeBtn) {
    e.preventDefault();
    updateCartItemQuantity(removeBtn.dataset.line, 0);
    return;
  }

  /* Qty + (scoped to .cart-drawer) */
  if (e.target.closest('.qty-plus')) {
    var row = e.target.closest('.item-row');
    if (!row) return;
    var input = row.querySelector('.qty-input');
    var line = row.dataset.line;
    if (!input || !line) return;
    updateCartItemQuantity(line, (parseInt(input.value, 10) || 1) + 1);
    return;
  }

  /* Qty − */
  if (e.target.closest('.qty-minus')) {
    var minusBtn = e.target.closest('.qty-minus');
    if (minusBtn.hasAttribute('disabled') || minusBtn.classList.contains('disabled')) {
      return;
    }
    var row2   = e.target.closest('.item-row');
    if (!row2) return;
    var input2 = row2.querySelector('.qty-input');
    var line2 = row2.dataset.line;
    if (!input2 || !line2) return;
    var qty = parseInt(input2.value, 10) || 1;
    updateCartItemQuantity(line2, qty > 1 ? qty - 1 : 0);
    return;
  }

  /* Gift packaging checkbox */
  var giftCheckbox = e.target.closest('.js-gift-packaging-checkbox');
  if (giftCheckbox) {
    var varId = giftCheckbox.dataset.variantId;
    if (!varId) return;

    if (giftCheckbox.checked) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: varId, quantity: 1 })
      })
      .then(function (r) { if (!r.ok) throw new Error(); return r.json(); })
      .then(function () { refreshCart(); })
      .catch(function (err) { 
        console.error('Gift packaging add error:', err);
        giftCheckbox.checked = false;
      });
    } else {
      /* Remove gift packaging - find line item first */
      fetch('/cart.js')
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          var line = null;
          for (var i = 0; i < cart.items.length; i++) {
            if (cart.items[i].variant_id == varId) {
              line = i + 1;
              break;
            }
          }
          if (line) {
            updateCartItemQuantity(line, 0);
          }
        });
    }
    return;
  }

  /* Upsell add-to-cart */
  var upsellBtn = e.target.closest('.js-upsell-add-to-cart');
  if (upsellBtn) {
    var variantId = upsellBtn.dataset.variantId;
    if (!variantId) return;
    upsellBtn.classList.add('loading');
    upsellBtn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Add to cart failed');
        return r.json();
      })
      .then(function () { return refreshCart(); })
      .then(function () {
        // Product was added from recommendations — reload the section so it
        // no longer shows the just-added item.
        document.querySelectorAll('product-recommendations[data-url]').forEach(function(el) {
          if (typeof el.forceLoadRecommendations === 'function') el.forceLoadRecommendations();
        });
      })
      .catch(function (err) { console.error('Upsell add to cart error:', err); })
      .finally(function () {
        upsellBtn.classList.remove('loading');
        upsellBtn.disabled = false;
      });
    return;
  }

  /* Apply discount code */
  var submitDiscount = e.target.closest('.submit-discount');
  if (submitDiscount) {
    var wrapper  = submitDiscount.closest('.custom-discount-form');
    var inputEl  = wrapper.querySelector('input');
    var errorEl2 = wrapper.querySelector('p.error');
    var code2    = inputEl ? inputEl.value.trim() : '';
    if (errorEl2) errorEl2.style.display = 'none';

    if (code2 && code2.length) {
      applyDiscountCode(code2, submitDiscount);
    } else if (errorEl2) {
      errorEl2.style.display = 'block';
      errorEl2.textContent = 'Enter a valid discount code or gift card';
    }
    return;
  }

  /* Remove discount code */
  var removeDiscount = e.target.closest('.remove-discount');
  if (removeDiscount) {
    removeDiscountCode(removeDiscount.dataset.code);
    return;
  }

  /* Discount toggle */
  var discountToggle = e.target.closest('.cart-footer-discount-toggle');
  if (discountToggle) {
    var wrapper = discountToggle.closest('.cart-footer-discount-wrapper');
    if (wrapper) {
      var discountForm = wrapper.querySelector('.cart-footer-discount-form');
      if (discountForm) {
        // Smoothly hide toggle (handled by CSS transitions)
        discountToggle.classList.add('collapsed');

        // Smoothly show form
        discountForm.classList.add('is-open');
        discountForm.style.maxHeight = discountForm.scrollHeight + 'px';
        discountForm.style.opacity = '1';

        var onTransitionEnd = function(event) {
          if (event.propertyName === 'max-height') {
            if (discountForm.classList.contains('is-open')) {
              discountForm.style.maxHeight = 'none';
            }
            discountForm.removeEventListener('transitionend', onTransitionEnd);
          }
        };
        discountForm.addEventListener('transitionend', onTransitionEnd);
      }
    }
    return;
  }

  /* Custom Variant Selector Toggle */
  var selectorToggle = e.target.closest('.js-custom-variant-selector .selector-toggle');
  if (selectorToggle) {
    var parent = selectorToggle.closest('.js-custom-variant-selector');
    var isOpen = parent.classList.contains('is-open');
    
    // Close other open selectors first
    document.querySelectorAll('.js-custom-variant-selector.is-open').forEach(function(el) {
      if (el !== parent) el.classList.remove('is-open');
    });

    parent.classList.toggle('is-open');
    selectorToggle.setAttribute('aria-expanded', !isOpen);
    return;
  }

  /* Custom Variant Selector Option Click */
  var option = e.target.closest('.selector-option');
  if (option) {
    selectCartVariantOption(option);
    return;
  }

  /* Click Outside to Close Selectors */
  if (!e.target.closest('.js-custom-variant-selector')) {
    document.querySelectorAll('.js-custom-variant-selector.is-open').forEach(function(el) {
      el.classList.remove('is-open');
    });
  }
});

/* ── delegated change handler (survives DOM refreshes) ────────── */

document.addEventListener('change', function (e) {
  // We handle variant switching via custom click handler now
});

/* Serialize variant swaps: run them strictly one at a time so concurrent changes on
   different products can't interleave cart mutations (which duplicated or dropped items). */
var variantUpdateQueue = Promise.resolve();

function updateCartItemVariant(oldVariantId, newVariantId, line) {
  if (!oldVariantId || !newVariantId) return variantUpdateQueue;

  variantUpdateQueue = variantUpdateQueue.then(function() {
    return fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        // Try matching by the exact line index first, fallback to filtering by variant ID.
        var item = null;
        if (line) {
          item = cart.items[parseInt(line, 10) - 1];
        }
        if (!item || String(item.variant_id) !== String(oldVariantId)) {
          item = cart.items.filter(function(it) {
            return String(it.variant_id) === String(oldVariantId);
          })[0];
        }
        
        if (!item) return;
        var qty = item.quantity;
        var oldKey = item.key;

        // Remove the old line first (by stable key), then add the new variant.
        // Chaining res.json() ensures the HTTP transaction is fully finished, releasing the Shopify session lock.
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: oldKey, quantity: 0 })
        })
        .then(function(res) {
          if (!res.ok) throw new Error('Failed to remove variant');
          return res.json();
        })
        .then(function() {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: newVariantId, quantity: qty })
          });
        })
        .then(function(res) {
          if (!res.ok) throw new Error('Failed to add new variant');
          return res.json();
        });
      })
      .then(function() { return refreshCart(); })
      .then(function() {
        if (document.querySelector('[data-cart-page-section]')) {
          refreshCartPage();
        }
      })
      .catch(function(err) {
        console.error('Error switching variant:', err);
        return refreshCart().then(function() {
          if (document.querySelector('[data-cart-page-section]')) {
            refreshCartPage();
          }
        });
      });
  });

  return variantUpdateQueue;
}

/* ── keyboard: Escape to close + focus trap ──────────────────── */

document.addEventListener('keydown', function (e) {
  /* Custom variant selector option — a plain <li>, so it gets no free
     Enter/Space-to-activate the way a <button> would. */
  if (e.key === 'Enter' || e.key === ' ') {
    var variantOption = e.target.closest('.selector-option');
    if (variantOption) {
      e.preventDefault();
      var selectorToggle = variantOption.closest('.js-custom-variant-selector').querySelector('.selector-toggle');
      selectCartVariantOption(variantOption);
      // The option is about to be hidden — return focus to the toggle
      // instead of leaving it stranded on a now-invisible element.
      if (selectorToggle) selectorToggle.focus();
      return;
    }
  }

  /* Discount code "Enter" key */
  if (e.key === 'Enter') {
    var discountInput = e.target.closest('input[name="custom-discount-input"]') || e.target.closest('#DiscountCode') || e.target.closest('.custom-discount-form input');
    if (discountInput) {
      e.preventDefault();
      var wrapper = discountInput.closest('.custom-discount-form');
      var btn = wrapper ? wrapper.querySelector('.submit-discount') : null;
      if (btn) btn.click();
      return;
    }
  }

  if (e.key === 'Escape' && document.body.classList.contains('open-drawer')) {
    closeCartDrawer();
    return;
  }

  /* Focus trap inside the drawer */
  if (e.key === 'Tab' && document.body.classList.contains('open-drawer')) {
    var drawer = document.querySelector('.cart-sidebar');
    if (!drawer) return;

    var focusable = drawer.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;

    var first = focusable[0];
    var last  = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
});

/* ── Product Recommendations Element ─────────────────────────── */

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', class extends HTMLElement {
    connectedCallback() {
      var self = this;
      if (this.closest('.cart-drawer') || this.closest('.cart-sidebar')) {
        self.loadRecommendations();
      } else {
        var observer = new IntersectionObserver(function(entries, observer) {
          if (entries[0].isIntersecting) {
            observer.unobserve(self);
            self.loadRecommendations();
          }
        }, { threshold: 0.1 });
        observer.observe(this);
      }
    }

    loadRecommendations() {
      var url = this.dataset.url;
      if (!url) return;
      // Skip if already loaded for this exact URL — prevents spurious reloads
      // triggered by DOM mutations, IntersectionObserver re-fires, or init
      // functions running after cart updates.
      // Call forceLoadRecommendations() when an intentional reload is needed.
      if (this._loadedUrl === url) return;
      this._loadedUrl = url;

      var self = this;
      fetch(url)
        .then(function(response) { return response.text(); })
        .then(function(html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var recommendations = doc.querySelector('product-recommendations');
          if (recommendations && recommendations.innerHTML.trim().length > 0) {
            self.innerHTML = recommendations.innerHTML;
          }
          initMatchItSlider();
          initCartDrawerSlider();
          if (typeof initProductCardSliders === 'function') {
            initProductCardSliders();
          }
          if (typeof quickAddButton === 'function') {
            quickAddButton();
          }
        })
        .catch(function(e) {
          // Reset so a retry is possible
          self._loadedUrl = null;
          console.error('Error loading recommendations:', e);
        });
    }

    forceLoadRecommendations() {
      this._loadedUrl = null;
      this.loadRecommendations();
    }
  });
}

/* ── USP Ticker ─────────────────────────────────────────────── */
var isUspPaused = false;

function initUspTicker() {
  document.querySelectorAll('[data-usp-ticker]').forEach(function(ticker) {
    // Clear existing interval if any
    if (ticker._uspInterval) {
      clearInterval(ticker._uspInterval);
      ticker._uspInterval = null;
    }

    var items = ticker.querySelectorAll('[data-usp-item]');
    if (items.length <= 1) return;

    var toggleBtn = ticker.closest('.cart-drawer-usps-wrapper').querySelector('.video-toggle');
    
    if (isUspPaused) {
      if (toggleBtn) toggleBtn.classList.add('is-paused');
      return;
    }

    if (toggleBtn) toggleBtn.classList.remove('is-paused');

    var currentIndex = 0;
    // Find already active item if any
    items.forEach(function(item, idx) {
      if (item.classList.contains('active')) currentIndex = idx;
    });

    ticker._uspInterval = setInterval(function() {
      if (isUspPaused) return;

      var currentItem = items[currentIndex];
      currentItem.classList.add('fade-out');

      setTimeout(function() {
        currentItem.classList.remove('active', 'fade-out');
        currentIndex = (currentIndex + 1) % items.length;
        var nextItem = items[currentIndex];
        nextItem.classList.add('active');
      }, 500);
    }, 3500);
  });
}
function initCartAccordion() {
  var accordions = document.querySelectorAll('.cart-drawer-accordion');
  if (!accordions.length) return;

  accordions.forEach(function(el) {
    var title = el.querySelector('.cart-drawer-accordion__title');
    var body = el.querySelector('.cart-drawer-accordion__body');
    if (!title || !body) return;

    // Prevent duplicate listeners
    if (title.hasAccordionListener) return;
    title.hasAccordionListener = true;

    // Set initial state
    var isOpen = el.classList.contains('is-open');
    if (isOpen) {
      body.style.maxHeight = 'none';
    } else {
      body.style.maxHeight = '0px';
    }

    title.addEventListener('click', function() {
      var currentlyOpen = el.classList.contains('is-open');

      if (currentlyOpen) {
        // Closing
        body.style.maxHeight = body.scrollHeight + 'px';
        body.offsetHeight; // Force reflow
        body.style.maxHeight = '0px';
        el.classList.remove('is-open');
      } else {
        // Opening
        el.classList.add('is-open');
        body.style.maxHeight = body.scrollHeight + 'px';

        var onTransitionEnd = function(event) {
          if (event.propertyName === 'max-height') {
            if (el.classList.contains('is-open')) {
              body.style.maxHeight = 'none';
            }
            body.removeEventListener('transitionend', onTransitionEnd);
          }
        };
        body.addEventListener('transitionend', onTransitionEnd);
      }
    });

    title.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        title.click();
      }
    });
  });
}
/* ── initialise ──────────────────────────────────────────────── */

updateCartCount();
initMatchItSlider();
initCartDrawerSlider();
initUspTicker();
initCartAccordion();

/* Ticker play/pause click handler */
document.addEventListener('click', function(e) {
  var toggle = e.target.closest('.video-toggle');
  if (toggle) {
    isUspPaused = !isUspPaused;
    
    // Update all toggles to maintain visual consistency
    document.querySelectorAll('.video-toggle').forEach(function(btn) {
      btn.classList.toggle('is-paused', isUspPaused);
    });
    
    if (isUspPaused) {
      document.querySelectorAll('[data-usp-ticker]').forEach(function(ticker) {
        if (ticker._uspInterval) {
          clearInterval(ticker._uspInterval);
          ticker._uspInterval = null;
        }
      });
    } else {
      initUspTicker();
    }
  }
});

function initDiscountInput() {
  document.querySelectorAll('.custom-discount-form').forEach((form) => {
    const input = form.querySelector('input[name="custom-discount-input"]');

    if (!input || input.dataset.initialized) return;

    input.dataset.initialized = 'true';

    const defaultPlaceholder = form.dataset.placeholderDefault || input.getAttribute('placeholder') || 'Add discount code or gift card';
    const typePlaceholder = form.dataset.placeholderType || 'Type...';

    const toggleActive = () => {
      var hasVal = input.value.trim().length > 0;
      var isFocused = document.activeElement === input;
      if (hasVal || isFocused) {
        form.classList.add('active');
        input.setAttribute('placeholder', typePlaceholder);
      } else {
        form.classList.remove('active');
        input.setAttribute('placeholder', defaultPlaceholder);
      }
    };

    input.addEventListener('input', toggleActive);
    input.addEventListener('focus', toggleActive);
    input.addEventListener('blur', function() {
      setTimeout(toggleActive, 200);
    });

    // Initial state
    toggleActive();
  });
}

document.addEventListener('DOMContentLoaded', initDiscountInput);